import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle rate limit specifically
        if (response.status === 429) {
          return rejectWithValue('Too many login attempts. Please try again later.');
        }
        return rejectWithValue(data.message || 'Login failed');
      }
      
      // Store complete user data in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Registration failed');
      }
      
      return {
        user: data.user,
        token: data.token,
        message: data.message
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const inviteUser = createAsyncThunk(
  'auth/inviteUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/users/invite`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(userData),
      });
      
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Invitation failed');
      }
      
      return {
        user: data.user,
        message: data.message
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const setPasswordOnInvite = createAsyncThunk(
  'auth/setPasswordOnInvite',
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Password setup failed');
      }
      
      // Store complete user data in localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data;
    } catch (error) {
      return rejectWithValue('Network error occurred. Please try again.');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      // Even if user doesn't exist, we return success to prevent email enumeration
      if (response.status === 200) {
        return data;
      }
      
      return rejectWithValue(data.message || 'Failed to send reset email');
    } catch (error) {
      return rejectWithValue('Network error occurred. Please try again.');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Password reset failed');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue('Network error occurred. Please try again.');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/users/profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profileData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update profile');
      }
      
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return data.user;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/users/password`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(passwordData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update password');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    resetEmailSent: false,
    passwordResetSuccess: false,
    inviteSuccess: false,
    passwordSetSuccess: false,
    showSessionExpiredDialog: false
  },
  reducers: {
    initializeAuth: (state) => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        state.token = token;
        state.user = JSON.parse(storedUser);
        state.isAuthenticated = true;
      }
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      // Clear all auth data from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    },
    clearResetStatus: (state) => {
      state.resetEmailSent = false;
      state.passwordResetSuccess = false;
    },
    clearInviteStatus: (state) => {
      state.inviteSuccess = false;
    },
    clearPasswordSetStatus: (state) => {
      state.passwordSetSuccess = false;
    },
    showSessionExpiredDialog: (state) => {
      state.showSessionExpiredDialog = true;
    },
    hideSessionExpiredDialog: (state) => {
      state.showSessionExpiredDialog = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        
        // Store token in localStorage
        localStorage.setItem('token', action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      
      .addCase(inviteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.inviteSuccess = false;
      })
      .addCase(inviteUser.fulfilled, (state) => {
        state.loading = false;
        state.inviteSuccess = true;
        state.error = null;
        showToast('User invited successfully', 'success');
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.inviteSuccess = false;
        showToast(action.payload, 'error');
      })
      
      .addCase(setPasswordOnInvite.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordSetSuccess = false;
      })
      .addCase(setPasswordOnInvite.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordSetSuccess = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        showToast('Password set successfully', 'success');
      })
      .addCase(setPasswordOnInvite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.passwordSetSuccess = false;
        showToast(action.payload, 'error');
      })
      
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetEmailSent = false;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetEmailSent = true;
        state.error = null;
        showToast('Reset password link has been sent to your email', 'success');
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.resetEmailSent = false;
        showToast(action.payload, 'error');
      })
      
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordResetSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.passwordResetSuccess = true;
        state.error = null;
        showToast('Password has been reset successfully', 'success');
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.passwordResetSuccess = false;
        showToast(action.payload, 'error');
      })
      
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  setUser, 
  setToken, 
  logout, 
  clearError, 
  initializeAuth, 
  clearResetStatus,
  clearInviteStatus,
  clearPasswordSetStatus,
  showSessionExpiredDialog,
  hideSessionExpiredDialog
} = authSlice.actions;
export default authSlice.reducer; 